import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Chip,
  CssBaseline,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import { useDispatch, useSelector } from "react-redux";
import { tokens } from "../../theme";
import {
  deleteHistory,
  fetchHistory,
  fetchHistoryForExport,
  getHistoryError,
  getHistoryStatus,
  getHistorySuccess,
  selectAllHistory,
  selectPagination,
  updateStatus,
} from "../../features/historySlice";
import { toast } from "react-toastify";
import { useRestaurant } from "../../hooks/RestaurantContext";
import Loading from "../../components/loading";
import Error from "../../components/Error";
import NoDataTable from "../../components/noDataTable";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import HitoryModal from "../../components/hitoryModal";
import { formatDateTime } from "../../global/functions/dateFormat";
import AppBarSearch from "../../global/appBarSearch";
import ButtonComponent from "../../components/ButtonComponent";
import { getSettings, selectAllSettings } from "../../features/settingSlice";
import dayjs from "dayjs";
import { CouponType } from "../../models/coupon.model";
import DatepickerFilter from "../../components/filters/datepickerFilter";
import FiltersDialog, {
  FiltersButton,
} from "../../components/filters/filtersDialog";
import FilterChipGroup from "../../components/filters/filterChipGroup";
import { HistorySortList, applySort } from "../../models/sort.model";
import {
  formatStatusText,
  getStatusColor,
  StatusList,
} from "../../models/status.model";
import ParagraphHighliter from "../../components/paragraphHighliter";
import CustomDialog from "../../components/cutomPopUp";
import { ResponsiveProvider } from "../../hooks/uiHook";
import { DaysFilterList } from "../../models/statistic.model";
import { useTranslation } from "react-i18next";
import AlertDialog from "../../components/alertDialog";

const History = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { restaurantId } = useRestaurant();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [openExportModal, setOpenExportModal] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [historyData, setHistoryData] = useState();
  const [statusFilter, setStatusFilter] = useState("");
  const [packFilter, setPackFilter] = useState("");
  const [methodFilter, setMethodFilter] = useState("");
  const [sortFilter, setSortFilter] = useState("");
  const [filtersDialogOpen, setFiltersDialogOpen] = useState(false);
  // Draft copies bound to the FiltersDialog controls; committed to the
  // applied state above (which feeds the fetchHistory effect below) only
  // when Appliquer is clicked.
  const [draftStatusFilter, setDraftStatusFilter] = useState("");
  const [draftPackFilter, setDraftPackFilter] = useState("");
  const [draftMethodFilter, setDraftMethodFilter] = useState("");
  const [draftSortFilter, setDraftSortFilter] = useState("");
  const settings = useSelector(selectAllSettings);
  const histories = useSelector(selectAllHistory);
  const pagination = useSelector(selectPagination);
  const error = useSelector(getHistoryError);
  const success = useSelector(getHistorySuccess);
  const status = useSelector(getHistoryStatus);
  const totalCount = pagination.totalRecords || 0;
  const startDateTime = new Date();
  const endDateTime = new Date();
  startDateTime.setHours(0, 0, 0, 0);
  endDateTime.setHours(23, 59, 59, 999);
  const [dateFilter, setDateFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ startDate: startDateTime, endDate: endDateTime });
  const [draftDateFilter, setDraftDateFilter] = useState("all");
  const [draftDateRange, setDraftDateRange] = useState({ startDate: startDateTime, endDate: endDateTime });

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    let start = "";
    let end = "";

    if (dateFilter === "custom" && dateRange.startDate && dateRange.endDate) {
      start = dateRange.startDate;
      end = dateRange.endDate;
    }

    dispatch(
      fetchHistory({
        restaurantId,
        search,
        limit,
        page: page + 1,
        status: statusFilter,
        packId: packFilter,
        methodId: methodFilter,
        filter: dateFilter,
        startDate: start,
        endDate: end,
      })
    );
    setHistoryData(null);
    setOpenModal(false);
  }, [
    dispatch,
    restaurantId,
    search,
    limit,
    page,
    statusFilter,
    packFilter,
    methodFilter,
    dateFilter,
    dateRange.startDate,
    dateRange.endDate,
  ]);

  useEffect(() => {
    dispatch(getSettings(restaurantId));
  }, [dispatch, restaurantId]);

  useEffect(() => {
    if (status === "deleteSuccess") {
      toast.success(success || t("common.delete"));
      if (historyData?._id === deleteTarget?._id) {
        setOpenModal(false);
        setHistoryData(null);
      }
      if (histories.length === 0 && page > 0) setPage(page - 1);
      dispatch(updateStatus());
    } else if (status === "deleteError") {
      toast.error(error);
      dispatch(updateStatus());
    }
  }, [status, success, error, dispatch, t, histories.length, page, historyData, deleteTarget]);

  const handleChangePage = useCallback(
    (event, newPage) => {
      const maxPage = Math.max(0, Math.ceil(totalCount / limit) - 1);
      const clampedPage = Math.min(newPage, maxPage);
      setPage(clampedPage);
    },
    [limit, totalCount]
  );

  const handleChangeRowsPerPage = useCallback((event) => {
    const newLimit = Number(event.target.value);
    setLimit(newLimit);
    setPage(0);
  }, []);

  const toQueryDate = (value) => {
    if (!value) return "";
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.toISOString() : "";
  };

  const buildExportQuery = (filtered) => {
    if (!filtered) {
      return { restaurantId, filter: "all" };
    }
    return {
      restaurantId,
      search,
      status: statusFilter,
      packId: packFilter,
      methodId: methodFilter,
      filter: dateFilter,
      startDate: toQueryDate(dateRange.startDate),
      endDate: toQueryDate(dateRange.endDate),
    };
  };

  const matchesExportFilters = (row) => {
    if (search) {
      const term = String(search).trim().toLowerCase();
      const name = String(row.name || "").toLowerCase();
      const number = String(row.commandNumber ?? "");
      if (!name.includes(term) && !number.includes(term)) return false;
    }
    if (statusFilter && row.status !== statusFilter) return false;
    if (packFilter) {
      const packId = String(row.pack?._id || "");
      const packLabel = String(row.pack?.label || "");
      if (packId !== String(packFilter) && packLabel !== String(packFilter)) {
        return false;
      }
    }
    if (methodFilter) {
      const methodId = String(row.method?._id || "");
      const methodLabel = String(row.method?.label || "");
      if (methodId !== String(methodFilter) && methodLabel !== String(methodFilter)) {
        return false;
      }
    }
    const bought = dayjs(row.boughtAt);
    if (!bought.isValid()) return dateFilter === "all";
    if (dateFilter === "today") return bought.isSame(dayjs(), "day");
    if (dateFilter === "week") {
      const now = dayjs();
      const start =
        now.day() === 0
          ? now.subtract(6, "day").startOf("day")
          : now.subtract(now.day() - 1, "day").startOf("day");
      const end = start.add(6, "day").endOf("day");
      return !bought.isBefore(start) && !bought.isAfter(end);
    }
    if (dateFilter === "month") return bought.isSame(dayjs(), "month");
    if (dateFilter === "custom") {
      if (dateRange.startDate && bought.isBefore(dayjs(dateRange.startDate).startOf("day"))) {
        return false;
      }
      if (dateRange.endDate && bought.isAfter(dayjs(dateRange.endDate).endOf("day"))) {
        return false;
      }
    }
    return true;
  };

  // Normalizes a dateRange field (can be "", null, a Date, or a dayjs
  // instance depending on where it came from) to a comparable timestamp, or
  // null when empty/invalid, so draft/applied date fields compare reliably.
  const dateValue = (value) => {
    if (!value) return null;
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.valueOf() : null;
  };

  // Live draft-vs-applied diff: Appliquer stays disabled until at least one
  // draft filter differs from what's currently applied, and re-disables
  // itself automatically if the user changes a filter back to match.
  const hasPendingChanges =
    draftStatusFilter !== statusFilter ||
    draftPackFilter !== packFilter ||
    draftMethodFilter !== methodFilter ||
    draftDateFilter !== dateFilter ||
    dateValue(draftDateRange.startDate) !== dateValue(dateRange.startDate) ||
    dateValue(draftDateRange.endDate) !== dateValue(dateRange.endDate) ||
    draftSortFilter !== sortFilter;

  // There is no sorting on this screen (server-side or otherwise) today, so
  // this Tri is applied client-side on the currently-loaded page only,
  // consistent with the frontend-only scope of the rest of this feature —
  // it doesn't touch the /history query params above.
  const sortedHistories = applySort(histories, sortFilter, {
    getDate: (row) => row.boughtAt,
    getTotal: (row) => row.total,
  });

  const formatDiscount = (row) => {
    if (!row.discountValue || row.discountValue <= 0) return "0";
    if (row.couponId?.couponType === CouponType.fixed) {
      return `- ${row.discountValue} ${row.currency || ""}`.trim();
    }
    return `- ${row.discountValue} %`;
  };

  const exportToCSV = (data, fileName) => {
    const header = [
      t("table.number"),
      t("table.orderStatus"),
      t("table.pack"),
      t("table.payment"),
      t("table.client"),
      t("table.tva"),
      t("table.discount"),
      t("table.totalHt"),
      t("table.total"),
      t("table.boughtAt"),
    ];

    const csv = [
      header.join(","),
      ...data.map((row) =>
        [
          row.commandNumber ?? "",
          t(`status.${row.status}`, { defaultValue: formatStatusText(row.status) }),
          row.pack?.label ?? "",
          row.method?.label ?? "",
          row.name ?? "",
          `${row.tvaAmount ?? 0} ${row.currency || ""}`.trim(),
          formatDiscount(row),
          Number(row.totalHT || 0).toFixed(2),
          `${Number(row.total || 0).toFixed(2)} ${row.currency || ""}`.trim(),
          formatDateTime(row.boughtAt),
        ]
          .map((field) => `"${String(field ?? "").replace(/"/g, '""')}"`)
          .join(",")
      ),
    ].join("\r\n");

    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExport = async (filtered) => {
    setExporting(filtered ? "filtered" : "all");
    try {
      const rows = await dispatch(
        fetchHistoryForExport(buildExportQuery(filtered))
      ).unwrap();
      const exported = filtered ? rows.filter(matchesExportFilters) : rows;
      if (!exported.length) {
        toast.error(t("common.noDataToExport"));
        return;
      }
      const stamp = dayjs().format("YYYY-MM-DD");
      exportToCSV(
        exported,
        filtered ? `commandes_filtrees_${stamp}.csv` : `commandes_${stamp}.csv`
      );
      toast.success(t("common.exportSuccess"));
      setOpenExportModal(false);
    } catch (error) {
      toast.error(error?.message || t("common.noDataToExport"));
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="main-application">
      <CssBaseline />
      <ResponsiveProvider maxWidth={900}>
        <AppBarSearch
          disableMobileDrawer
          searchText={t("search.byClient")}
          handleSearch={(e) => setSearchInput(e.target.value)}
          title={t("pages.myOrders")}
          onExport={() => setOpenExportModal(true)}
          children={
            <>
              <FiltersButton
                onClick={() => {
                  setDraftStatusFilter(statusFilter);
                  setDraftPackFilter(packFilter);
                  setDraftMethodFilter(methodFilter);
                  setDraftDateFilter(dateFilter);
                  setDraftDateRange(dateRange);
                  setDraftSortFilter(sortFilter);
                  setFiltersDialogOpen(true);
                }}
                activeCount={
                  (statusFilter ? 1 : 0) +
                  (packFilter ? 1 : 0) +
                  (methodFilter ? 1 : 0) +
                  (dateFilter !== "all" ? 1 : 0) +
                  (sortFilter ? 1 : 0)
                }
              />
            </>
          }
        />
      </ResponsiveProvider>
      <FiltersDialog
        open={filtersDialogOpen}
        onClose={() => setFiltersDialogOpen(false)}
        onReset={() => {
          setDraftStatusFilter("");
          setStatusFilter("");
          setDraftPackFilter("");
          setPackFilter("");
          setDraftMethodFilter("");
          setMethodFilter("");
          setDraftDateFilter("all");
          setDateFilter("all");
          setDraftDateRange({ startDate: "", endDate: "" });
          setDateRange({ startDate: "", endDate: "" });
          setDraftSortFilter("");
          setSortFilter("");
          setFiltersDialogOpen(false);
        }}
        onApply={() => {
          setStatusFilter(draftStatusFilter);
          setPackFilter(draftPackFilter);
          setMethodFilter(draftMethodFilter);
          setDateFilter(draftDateFilter);
          setDateRange(draftDateRange);
          setSortFilter(draftSortFilter);
          setFiltersDialogOpen(false);
        }}
        disableApply={!hasPendingChanges}
      >
        <FilterChipGroup
          label={t("filters.allStatuses")}
          options={StatusList}
          value={draftStatusFilter}
          onChange={setDraftStatusFilter}
          allowAll
        />
        <FilterChipGroup
          label={t("filters.allPacks")}
          options={settings.pack}
          value={draftPackFilter}
          onChange={setDraftPackFilter}
          allowAll
        />
        <FilterChipGroup
          label={t("filters.allMethods")}
          options={settings.method}
          value={draftMethodFilter}
          onChange={setDraftMethodFilter}
          allowAll
        />
        <FilterChipGroup
          label={t("filters.filterBy")}
          options={DaysFilterList}
          value={draftDateFilter}
          onChange={setDraftDateFilter}
        />
        {draftDateFilter === "custom" && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            <DatepickerFilter
              label={t("filters.startDate")}
              value={
                draftDateRange.startDate ? dayjs(draftDateRange.startDate) : null
              }
              onChange={(newValue) =>
                setDraftDateRange({ ...draftDateRange, startDate: newValue })
              }
              maxDate={
                draftDateRange.endDate &&
                dayjs(draftDateRange.endDate).isBefore(dayjs())
                  ? dayjs(draftDateRange.endDate)
                  : dayjs()
              }
            />
            <DatepickerFilter
              label={t("filters.endDate")}
              value={draftDateRange.endDate ? dayjs(draftDateRange.endDate) : null}
              onChange={(newValue) =>
                setDraftDateRange({ ...draftDateRange, endDate: newValue })
              }
              minDate={
                draftDateRange.startDate ? dayjs(draftDateRange.startDate) : null
              }
              maxDate={dayjs()}
            />
          </Box>
        )}
        <FilterChipGroup
          label={t("filters.sortBy")}
          options={HistorySortList}
          value={draftSortFilter}
          onChange={setDraftSortFilter}
        />
      </FiltersDialog>
      <main>
        <Box sx={{ mt: 2, mb: 2 }}>
          <Paper sx={{ width: "100%", overflow: "hidden" }}>
            <TableContainer>
              <Table aria-label="command table">
                <TableHead style={{ backgroundColor: colors.primary[700] }}>
                  <TableRow>
                    <TableCell>{t("table.preview")}</TableCell>
                    <TableCell>{t("table.number")}</TableCell>
                    <TableCell>{t("table.orderStatus")}</TableCell>
                    <TableCell>{t("table.pack")}</TableCell>
                    <TableCell>{t("table.payment")}</TableCell>
                    <TableCell>{t("table.client")}</TableCell>
                    <TableCell>{t("table.tva")}</TableCell>
                    <TableCell>{t("table.discount")}</TableCell>
                    <TableCell>{t("table.totalHt")}</TableCell>
                    <TableCell>{t("table.total")}</TableCell>
                    <TableCell align="right">{t("table.boughtAt")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {status === "loading" ? (
                    <TableRow>
                      <TableCell colSpan={11}>
                        <Loading size={9} />
                      </TableCell>
                    </TableRow>
                  ) : status === "fetchError" ? (
                    <TableRow>
                      <TableCell colSpan={11}>
                        <Error>{error}</Error>
                      </TableCell>
                    </TableRow>
                  ) : sortedHistories.length > 0 ? (
                    sortedHistories.map((row) => (
                      <TableRow
                        key={row._id}
                        sx={{
                          backgroundColor: colors.primary[400],
                        }}
                      >
                        <TableCell>
                          <IconButton
                            onClick={() => {
                              setHistoryData(row);
                              setOpenModal(true);
                            }}
                          >
                            <VisibilityIcon />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => {
                              setDeleteTarget(row);
                              setOpenDelete(true);
                            }}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </TableCell>
                        <TableCell align="left">{row.commandNumber}</TableCell>
                        <TableCell>
                          <Chip
                            variant="outlined"
                            label={formatStatusText(row.status)}
                            color={getStatusColor(row.status)}
                          />
                        </TableCell>
                        <TableCell>{row.pack.label}</TableCell>
                        <TableCell>{row.method.label}</TableCell>
                        <TableCell>
                          <ParagraphHighliter
                            title={row?.name}
                            search={search}
                          />
                        </TableCell>
                        <TableCell>{`${row.tvaAmount} ${row.currency}`}</TableCell>
                        <TableCell>
                          {row.discountValue && row.discountValue > 0
                            ? row.couponId?.couponType === CouponType.fixed
                              ? `- ${row.discountValue} ${row.currency}`
                              : `- ${row.discountValue} %`
                            : "0"}
                        </TableCell>
                        <TableCell>{row.totalHT.toFixed(2)}</TableCell>
                        <TableCell>{`${row.total.toFixed(2)} ${
                          row.currency
                        }`}</TableCell>
                        <TableCell align="right">
                          {formatDateTime(row.boughtAt)}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <NoDataTable cols={10} />
                  )}
                </TableBody>
                {openModal && historyData && (
                  <HitoryModal
                    open={openModal}
                    handleClose={() => setOpenModal(false)}
                    data={historyData}
                  />
                )}
              </Table>
            </TableContainer>
            <TablePagination
              sx={{ backgroundColor: colors.primary[700] }}
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={totalCount}
              rowsPerPage={limit}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </Box>
        <AlertDialog
          open={openDelete}
          handleClose={() => setOpenDelete(false)}
          title={t("common.delete")}
          message={t("common.deleteOrderConfirm", {
            number: deleteTarget?.commandNumber ?? "",
          })}
          deleteData={() =>
            deleteTarget &&
            dispatch(deleteHistory({ id: deleteTarget._id, restaurantId }))
          }
        />
        <CustomDialog
          open={openExportModal}
          handleClose={() => !exporting && setOpenExportModal(false)}
          title={t("actions.exportCsv")}
          cancelText={t("common.cancel")}
          noConfirm={false}
        >
          <Box
            sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 1 }}
          >
            <ButtonComponent
              type="button"
              color="info"
              onClick={() => !exporting && handleExport(false)}
              text={t("common.exportAll")}
              loading={exporting === "all"}
            />
            <ButtonComponent
              type="button"
              onClick={() => !exporting && handleExport(true)}
              text={t("common.exportFiltered")}
              loading={exporting === "filtered"}
            />
          </Box>
        </CustomDialog>
      </main>
    </div>
  );
};

export default History;
